import { ASK_QUESTION_KEY } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import {
	isStoreAssistant,
	type StoreAssistant,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { isConfluenceCreatableAction, isConfluenceUpdatableAction } from './index';

type ConfluenceResultAcceptanceAttributesForAskQuestionParams = {
	messages: StoreChatItem[];
	contentId: string;
};

export type DisambiguationAttributes = {
	disambiguationUsedOnCreation: boolean;
	disambiguationUsedOnEdit: boolean;
};

type ActionDataWithContentId = {
	contentId?: string | number;
	metadata?: {
		contentId?: string | number;
	};
};

const getActions = (message: StoreAssistant): MessageAction[] =>
	message.content?.message?.message?.actions ?? [];

const isConfluenceCreateAction = (action: MessageAction): boolean =>
	isConfluenceCreatableAction({ key: action.key });

const isConfluenceEditAction = (action: MessageAction): boolean =>
	isConfluenceUpdatableAction({ key: action.key });

const getActionContentId = (action: MessageAction): string | undefined => {
	const data = action.data as ActionDataWithContentId | undefined;
	const contentId = data?.contentId ?? data?.metadata?.contentId;

	return contentId === undefined ? undefined : String(contentId);
};

const getPreviousAssistantActions = (
	messages: StoreChatItem[],
	beforeIndex: number,
): MessageAction[] => {
	for (let i = beforeIndex - 1; i >= 0; i--) {
		const message = messages[i];
		if (!isStoreAssistant(message)) {
			continue;
		}

		const actions = getActions(message);
		if (actions.length) {
			return actions;
		}
	}

	return [];
};

const getCreateMessageIndexByContentId = (messages: StoreChatItem[], contentId: string): number => {
	for (let i = messages.length - 1; i >= 0; i--) {
		const message = messages[i];
		if (!isStoreAssistant(message)) {
			continue;
		}

		const selectedAction = getActions(message)[0];
		if (
			selectedAction &&
			isConfluenceCreateAction(selectedAction) &&
			getActionContentId(selectedAction) === contentId
		) {
			return i;
		}
	}

	return -1;
};

const hasAskQuestionBeforeMessageAtIndex = (
	messages: StoreChatItem[],
	messageIndex: number,
): boolean =>
	getPreviousAssistantActions(messages, messageIndex).some(
		(action) => action.key === ASK_QUESTION_KEY,
	);

const wasDisambiguationUsedOnCreation = (messages: StoreChatItem[], contentId: string): boolean => {
	const selectedIndex = getCreateMessageIndexByContentId(messages, contentId);
	if (selectedIndex < 0) {
		return false;
	}

	return hasAskQuestionBeforeMessageAtIndex(messages, selectedIndex);
};

const wasDisambiguationUsedOnEdit = (messages: StoreChatItem[], contentId: string): boolean => {
	return messages.some((message, index) => {
		if (!isStoreAssistant(message)) {
			return false;
		}

		const actions = getActions(message);
		const hasMatchingEdit = actions.some(
			(action) => isConfluenceEditAction(action) && getActionContentId(action) === contentId,
		);

		return hasMatchingEdit && hasAskQuestionBeforeMessageAtIndex(messages, index);
	});
};

export function getConfluenceResultAcceptanceAttributesForAskQuestion({
	messages,
	contentId,
}: ConfluenceResultAcceptanceAttributesForAskQuestionParams): DisambiguationAttributes {
	if (!contentId) {
		return {
			disambiguationUsedOnCreation: false,
			disambiguationUsedOnEdit: false,
		};
	}

	return {
		disambiguationUsedOnCreation: wasDisambiguationUsedOnCreation(messages, contentId),
		disambiguationUsedOnEdit: wasDisambiguationUsedOnEdit(messages, contentId),
	};
}
